<script setup>
import axios from 'axios'
import { isEqual, isNull } from 'lodash'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

import HomePageApiHandler from '@/api/homepage'
import { WORKSPACE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import {
  AI_REPORT_DEBUG_REQUEST_KEY,
  AI_REPORT_GENERATION_STATUSES,
  AI_REPORT_NOTHING_TO_ANALYSE_STATUS,
  AI_REPORT_POLLING_INTERVAL_MS,
  AI_REPORT_RESPONSE_KEYS,
  AI_REPORT_SECTION_KEY_BY_TYPE,
  AI_REPORT_TYPES
} from '@/utils/home-page/ai-report'
import { useAiReportDebugSettings } from '@/utils/home-page/ai-report-debug-settings'
import { useAiReportFilters } from '@/utils/home-page/ai-report-filters'
import { checkResponseStatus, RESPONSE_STATUSES } from '@/utils/response-statuses'
import { useLoadingState } from '@/utils/use-loading-state'

import { HOMEPAGE_AI_REPORTS_TEST_ID } from '@/components/home-page/jest-helpers'

defineOptions({
  name: 'HomePageAiReports'
})

/*
 * Owns the kept reports of the selection: the read on selection and filter changes, the polling
 * of a running generation and the generate call. It renders no card: the default slot gets the
 * Alignment state for the figures of the header card and the Alignment report dialog (D-27).
 */
const props = defineProps({
  selectedUserOrGroup: {
    type: [String, Number],
    default: null
  },

  userSelected: {
    type: Boolean
  },

  workspaceIds: {
    type: Array,
    required: true
  },

  intervalTypeId: {
    type: Number,
    required: true
  },

  selectedIntervalIds: {
    type: Array,
    required: true
  },

  gradeTypes: {
    type: Array,
    required: true
  }
})

const { ALIGNMENT, ENGAGEMENT } = AI_REPORT_TYPES
const { GENERATING } = AI_REPORT_GENERATION_STATUSES

const store = useStore()

const homepageApi = new HomePageApiHandler()

const { readSettings: readDebugSettings } = useAiReportDebugSettings()

const { isLoading, toggleLoading } = useLoadingState()

const reports = ref(null)

const nothingToAnalyseReportTypes = ref([])

// the report types whose generate request is in flight: the dialog shows the generating state
// from the click, before the 202 answers (FR-0.4)
const pendingGenerationReportTypes = ref([])

const isAlignmentNothingToAnalyse = computed(() => {
  return nothingToAnalyseReportTypes.value.includes(ALIGNMENT)
})

const allWorkspaceIds = computed(() => {
  return store.state.workspaces.workspaces.map(workspace => workspace[WORKSPACE_ENTITY_KEYS.ID])
})

const hasSelection = computed(() => !isNull(props.selectedUserOrGroup))

const { selection, readQuery, isCustomRangeSelected } = useAiReportFilters({
  selectedUserOrGroup: () => props.selectedUserOrGroup,
  userSelected: () => props.userSelected,
  workspaceIds: () => props.workspaceIds,
  allWorkspaceIds,
  intervalTypeId: () => props.intervalTypeId,
  selectedIntervalIds: () => props.selectedIntervalIds,
  gradeTypes: () => props.gradeTypes
})

const getSection = reportType => {
  if (isNull(reports.value)) {
    return null
  }

  return reports.value[AI_REPORT_SECTION_KEY_BY_TYPE[reportType]] ?? null
}

// the section is served on a custom range too: the figure keeps its value and opens nothing (FR-3.4)
const alignmentSection = computed(() => getSection(ALIGNMENT))

const isAlignmentAvailable = computed(() => !isCustomRangeSelected.value)

const isGenerationRunning = computed(() => {
  return [ALIGNMENT, ENGAGEMENT].some(reportType => {
    return getSection(reportType)?.[AI_REPORT_RESPONSE_KEYS.GENERATION_STATUS] === GENERATING
  })
})

const isAlignmentGenerating = computed(() => {
  return (
    pendingGenerationReportTypes.value.includes(ALIGNMENT) ||
    alignmentSection.value?.[AI_REPORT_RESPONSE_KEYS.GENERATION_STATUS] === GENERATING
  )
})

// a request still in flight at unmount must neither poll nor touch the state when it resolves
let isUnmounted = false

//#region polling
let pollingTimeoutId = null

const stopPolling = () => {
  clearTimeout(pollingTimeoutId)
  pollingTimeoutId = null
}

const schedulePolling = () => {
  stopPolling()

  if (isUnmounted || !isGenerationRunning.value) {
    return
  }

  pollingTimeoutId = setTimeout(async () => {
    await loadReports({ silent: true })
  }, AI_REPORT_POLLING_INTERVAL_MS)
}
//#endregion

let lastRequestId = 0

// a generate request settled after the selection changed must not touch the new selection's state
let selectionVersion = 0

const loadReports = async ({ silent = false } = {}) => {
  stopPolling()

  lastRequestId += 1
  const requestId = lastRequestId

  if (!hasSelection.value) {
    reports.value = null
    return
  }

  if (!silent) {
    toggleLoading(true)
  }

  try {
    const data = await homepageApi.getReport(readQuery.value)

    if (requestId !== lastRequestId) {
      return
    }

    reports.value = data
    schedulePolling()
  } catch (error) {
    if (axios.isCancel(error) || requestId !== lastRequestId) {
      return
    }

    // 403: the reader may see no report of the selection, the figures show no report
    const isForbidden = checkResponseStatus({ error, status: RESPONSE_STATUSES.FORBIDDEN })

    // a failed poll keeps the kept reports on screen and tries again on the next tick
    if (silent && !isForbidden) {
      schedulePolling()
      return
    }

    reports.value = null

    if (!isForbidden) {
      handleError({ error })
    }
  } finally {
    if (requestId === lastRequestId) {
      toggleLoading(false)
    }
  }
}

const setReportTypeFlag = ({ flags, reportType, value }) => {
  const otherReportTypes = flags.value.filter(type => type !== reportType)

  flags.value = value ? [...otherReportTypes, reportType] : otherReportTypes
}

const onGenerateRequested = async ({ reportType, periodId = null }) => {
  setReportTypeFlag({ flags: nothingToAnalyseReportTypes, reportType, value: false })
  setReportTypeFlag({ flags: pendingGenerationReportTypes, reportType, value: true })

  const requestSelectionVersion = selectionVersion
  const isStale = () => isUnmounted || requestSelectionVersion !== selectionVersion

  try {
    const payload = {
      reportTypeId: reportType,
      periodId,
      ...selection.value
    }

    // the saved debug settings go with every Alignment generation of the person, read at the
    // click so that a save in the debug dialog applies at once (FR-0.14, FR-0.9)
    const debugSettings = reportType === ALIGNMENT ? readDebugSettings() : null

    if (debugSettings) {
      payload[AI_REPORT_DEBUG_REQUEST_KEY] = debugSettings
    }

    const data = await homepageApi.generate(payload)

    if (isStale()) {
      return
    }

    // a filter read in flight is superseded by a fresh one: it applies the new filter values,
    // clears the skeleton and shows the generation the server has just started
    if (isLoading.value) {
      await loadReports()
      return
    }

    // a poll still in flight was read before this generation started and must not overwrite its status
    lastRequestId += 1

    const section = getSection(reportType)

    if (section) {
      section[AI_REPORT_RESPONSE_KEYS.GENERATION_STATUS] =
        data?.[AI_REPORT_RESPONSE_KEYS.GENERATION_STATUS] ?? GENERATING
    }

    schedulePolling()
  } catch (error) {
    if (isStale()) {
      return
    }

    if (checkResponseStatus({ error, status: AI_REPORT_NOTHING_TO_ANALYSE_STATUS })) {
      setReportTypeFlag({ flags: nothingToAnalyseReportTypes, reportType, value: true })
      return
    }

    // 409: a generation of the same report runs, the read shows it as generating
    if (checkResponseStatus({ error, status: RESPONSE_STATUSES.CONFLICT })) {
      await loadReports({ silent: true })
      return
    }

    handleError({ error })
  } finally {
    if (!isStale()) {
      setReportTypeFlag({ flags: pendingGenerationReportTypes, reportType, value: false })
    }
  }
}

// the first click on the figure without a kept report and "Recalculate" in the dialog (FR-0.4, FR-0.5)
const generateAlignment = async () => {
  await onGenerateRequested({ reportType: ALIGNMENT })
}

watch(
  () => props.selectedUserOrGroup,
  () => {
    selectionVersion += 1
    nothingToAnalyseReportTypes.value = []
    pendingGenerationReportTypes.value = []
  }
)

// a filter change is one read and never a generation
watch(readQuery, async (newQuery, oldQuery) => {
  if (isEqual(newQuery, oldQuery)) {
    return
  }

  await loadReports()
})

onMounted(async () => {
  await loadReports()
})

onBeforeUnmount(() => {
  isUnmounted = true
  // drops the result of a read still in flight
  lastRequestId += 1
  stopPolling()
})
</script>

<template>
  <div
    :data-auto-testid="HOMEPAGE_AI_REPORTS_TEST_ID"
    :data-testid="HOMEPAGE_AI_REPORTS_TEST_ID"
    class="hpar-Reports"
  >
    <slot
      :alignment-section="alignmentSection"
      :generate-alignment="generateAlignment"
      :is-alignment-available="isAlignmentAvailable"
      :is-alignment-generating="isAlignmentGenerating"
      :is-alignment-nothing-to-analyse="isAlignmentNothingToAnalyse"
      :is-loading="isLoading"
    />
  </div>
</template>

<style lang="scss" scoped>
.hpar-Reports {
  display: flex;
  flex-direction: column;
}
</style>
